import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CommunicationPreferences } from '../../models/api/core/comm-preferences/communication-preferences';
import { Action } from '../../models/core/action';
import { CasePipe } from '../../pipes/case.pipe';
import { ValidatorsEx } from '../../validators/validators-ex';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { ActionButtonComponent } from '../core/buttons/action-button.component';
import { ToolbarButtonComponent } from '../core/buttons/toolbar-button.component';
import { InputComponent } from '../core/controls/input.component';
import { RadioButtonComponent } from '../core/controls/radio-button.component';
import { SelectComponent } from '../core/controls/select.component';
import { HelpIconComponent } from '../core/labels/help-icon.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { DialogComponent } from '../core/panels/dialog.component';
import { MenuComponent } from '../core/panels/menu.component';
import { EditPreferencesComponent } from './edit-communication-preferences.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';
import { HomeCardComponent } from '../home/home-card.component';
import { PageHeaderComponent } from '../core/labels/page-header.component';
@Component({
  selector: 'app-communication-preferences',
  imports: [
    ButtonComponent,
    ActionButtonComponent,
    ActionRowComponent,
    ToolbarButtonComponent,
    DialogComponent,
    MenuComponent,
    RouterModule,
    EditPreferencesComponent,
    SelectComponent,
    InputComponent,
    RadioButtonComponent,
    ReactiveFormsModule,
    HelpIconComponent,
    CasePipe,
    RequiredFieldsNoteComponent,
    HomeCardComponent,
    PageHeaderComponent,
  ],
  template: `
    <app-page-header label="Communication Preferences" headerSize="h1" />
    <div class="tw-grow tw-w-full tw-justify-center">
      <app-home-card type="Large">
        <div class="tw-p-8">
          <app-action-row class="tw-w-full tw-mb-4">
            <app-button
              right1
              label="Add communication preference"
              (click)="openAddDialog()"
            />
          </app-action-row>
          <div
            role="table"
            aria-label="Communication preferences"
            class="md:tw-hidden tw-flex tw-flex-col tw-gap-4"
          >
            @for (item of contactMethod; track item; let i = $index) {
              <div role="row" class="tw-border-b tw-pb-4 tw-flex tw-flex-col tw-gap-2">
                <div role="cell">
                  <span class="tw-font-bold">Method: </span>
                  <span class="tw-whitespace-nowrap">
                    {{ item.method | case: 'upper' }}
                    @if (!item.contactUid) {
                      <app-help-icon
                        helpText="The email address associated with your user account cannot be edited or deleted."
                      />
                    }
                  </span>
                </div>
                <div role="cell">
                  <span class="tw-font-bold">Value: </span>
                  {{ item.value }}
                </div>
                <div role="cell">
                  <span class="tw-font-bold">Use for Authentication?: </span>
                  @if (item.auth === 'Y') {
                    Yes
                  } @else {
                    No
                  }
                </div>
                <div
                  role="cell"
                  class="tw-flex tw-justify-end tw-flex-wrap tw-items-center tw-gap-3 tw-py-1.5"
                >
                  <app-action-button
                    [ariaLabel]="getRowMenuLabel(item)"
                    [menu]="rowMenu"
                    [row]="item"
                    (click)="openRowMenu($event, item)"
                  />
                </div>
              </div>
            }
          </div>
          <app-menu
            [items]="rowMenuItems"
            #rowMenu
            (click)="onRowAction($event)"
          />
          <div class="tw-hidden md:tw-block tw-overflow-x-auto">
            <div
              role="table"
              aria-label="Communication preferences"
              class="tw-grid tw-grid-cols-[100px_1fr_180px_80px_80px] tw-gap-2 tw-items-center tw-min-w-[800px]"
            >
              <div role="row" class="tw-contents">
                <div role="columnheader" class="tw-font-bold">Method</div>
                <div role="columnheader" class="tw-font-bold">Value</div>
                <div role="columnheader" class="tw-font-bold">Use for Authentication?</div>
                <div role="columnheader" class="tw-font-bold">Edit</div>
                <div role="columnheader" class="tw-font-bold">Delete</div>
              </div>
              @for (item of contactMethod; track item; let i = $index) {
                <div role="row" class="tw-contents">
                  <div role="cell" class="tw-whitespace-nowrap">
                    {{ item.method | case: 'upper' }}
                    @if (!item.contactUid) {
                      <app-help-icon
                        helpText="The email address associated with your user account cannot be edited or deleted."
                      />
                    }
                  </div>
                  <div role="cell" class="tw-truncate">{{ item.value }}</div>
                  <div role="cell">
                    @if (item.auth === 'Y') {
                      Yes
                    } @else {
                      No
                    }
                  </div>
                  <div role="cell">
                    <app-button
                      right1
                      label="Edit"
                      [ariaLabel]="getRowActionLabel(item, 'Edit')"
                      [disabled]="item.contactUid === 0"
                      (click)="selectedDataToUpdate(item)"
                    />
                  </div>
                  <div role="cell">
                    <app-toolbar-button
                      icon="fa-trash-can fa-light"
                      [ariaLabel]="getRowActionLabel(item, 'Delete')"
                      [disabled]="item.contactUid === 0"
                      (click)="deletePreferences(item, i)"
                    />
                  </div>
                </div>
                <div class="tw-border-b tw-col-span-5" aria-hidden="true"></div>
              }
            </div>
          </div>
          <br />
          <app-action-row class="tw-w-full">
            <app-button
              right1
              label="Return to Preferences"
              routerLink="/preferences"
            />
          </app-action-row>
        </div>
      </app-home-card>
    </div>
    <app-dialog
      #add
      [visible]="dialog"
      [closable]="false"
      label="Manage Communication Preferences"
    >
      <ng-template #content>
        <div class="tw-p-3 md:tw-p-8 tw-flex tw-flex-wrap">
          <app-required-fields-note />
          <div
            class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 md:tw-gap-2 tw-items-center tw-w-full tw-mt-3 md:tw-mt-0"
          >
            <div class="tw-hidden md:tw-block"></div>
            <div class="tw-hidden md:tw-block"></div>
            <div class="tw-hidden md:tw-block"></div>
            <div>
              <app-select
                [control]="fg.controls.method"
                label="Method"
                optionLabel="method"
                optionValue="code"
                [options]="options"
                [filter]="false"
                [virtualScroll]="false"
              />
            </div>
            <div>
              <app-input
                [control]="fg.controls.value"
                label="Value"
                class="tw-flex tw-flex-wrap"
              />
            </div>
            <div>
              <app-radio-button
                [control]="fg.controls.useForAuth"
                label="Use for Authentication?"
                optionLabel="auth"
                optionValue="code"
                [options]="option"
              />
            </div>
          </div>
        </div>
        <app-action-row class="tw-w-full">
          <app-button
            right1
            label="Save"
            (click)="addPreferences()"
            [disabled]="!fg.valid"
          />
          <app-button
            right2
            emphasis="Low"
            label="Cancel"
            (click)="dialog = false"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
    <app-dialog
      #editPreferences
      [visible]="editDialog"
      [closable]="false"
      label="Manage Communication Preferences"
    >
      <ng-template #content>
        <app-edit-preferences
          #updateData
          [dataSource]="selectedRow"
          (close)="updatePreferences($event)"
        />
      </ng-template>
    </app-dialog>
  `,
  styles: ``,
})
export class CommunicationPreferencesComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  contactMethod: CommunicationPreferences[] = [];
  selectedRow: any;
  dialog = false;
  editDialog = false;
  row: CommunicationPreferences;
  error: any;

  options = [
    { method: 'Email', code: 'EMAIL' },
    { method: 'Text', code: 'TEXT' },
  ];
  option = [
    { auth: 'Yes', code: 'Y' },
    { auth: 'No', code: 'N' },
  ];

  @ViewChild('updateData')
  updateDialogData: EditPreferencesComponent;

  @ViewChild('rowMenu')
  rowMenu: MenuComponent;

  rowMenuItems: Action[] = [];

  fg = new FormGroup({
    method: new FormControl<string>('', Validators.required),
    value: new FormControl<string>(null, Validators.required),
    useForAuth: new FormControl<string>('', Validators.required),
  });

  async ngOnInit() {
    await this.api.communicationPreferences.get().then((preference) => {
      this.contactMethod = preference;
    });
    this.dispose.subs = this.fg
      .get('method')
      ?.valueChanges.subscribe((value) => this.updateValueValidators(value));
  }

  async addPreferences() {
    this.row = {
      method: this.fg.get('method').value,
      value: this.fg.get('value').value,
      auth: this.fg.get('useForAuth').value,
    };

    this.error = null;
    try {
      this.row.contactUid = await this.api.communicationPreferences.add(
        this.row,
      );
      this.dialog = false;
      if (this.row.method === 'EMAIL') {
        this.row.value = this.maskEmail(this.row.value);
      } else {
        this.row.value = this.maskPhone(this.row.value);
      }
      this.contactMethod.push(this.row);
      this.fg.reset();
    } catch (e) {
      this.error = e;
    }
  }
  async deletePreferences(item: CommunicationPreferences, index: number) {
    try {
      this.api.communicationPreferences.delete(item.contactUid);
    } catch (e) {
      this.error = e;
    }
    this.contactMethod.splice(index, 1);
  }
  updatePreferences(contact: CommunicationPreferences) {
    if (!contact) {
      this.editDialog = false;
    } else {
      if (contact.method.toLocaleUpperCase() === 'EMAIL') {
        contact.value = this.maskEmail(contact.value);
      } else {
        contact.value = this.maskPhone(contact.value);
      }
      this.editDialog = false;
      this.contactMethod.find(
        (item) => item.contactUid == contact.contactUid,
      ).value = contact.value;
    }
  }

  selectedDataToUpdate(item: CommunicationPreferences) {
    this.selectedRow = item;
    this.editDialog = true;
    this.updateDialogData.setFormControlValues(this.selectedRow);
  }

  openAddDialog(): void {
    this.fg.reset();
    this.dialog = true;
  }

  getRowActionLabel(
    item: CommunicationPreferences,
    action: 'Edit' | 'Delete',
  ): string {
    const preferenceType = this.getMethodPreferenceLabel(item.method);
    const parts = [`${action} ${preferenceType}`];

    if (item.value) {
      parts.push(item.value);
    }
    if (item.auth === 'Y') {
      parts.push('used for authentication');
    }
    if (item.contactUid === 0) {
      parts.push(action === 'Edit' ? 'cannot be edited' : 'cannot be deleted');
    }

    return parts.join(', ');
  }

  getRowMenuLabel(item: CommunicationPreferences): string {
    const preferenceType = this.getMethodPreferenceLabel(item.method);
    const parts = [`More actions for ${preferenceType}`];

    if (item.value) {
      parts.push(item.value);
    }
    if (item.auth === 'Y') {
      parts.push('used for authentication');
    }
    if (item.contactUid === 0) {
      parts.push('cannot be edited or deleted');
    }

    return parts.join(', ');
  }

  getRowActions(item: CommunicationPreferences): Action[] {
    return [
      { label: 'Edit', key: 'edit', disabled: item.contactUid === 0 },
      { label: 'Delete', key: 'delete', disabled: item.contactUid === 0 },
    ];
  }

  openRowMenu(event: Event, item: CommunicationPreferences): void {
    this.rowMenuItems = this.getRowActions(item);
    this.rowMenu.items = this.rowMenuItems;
    this.rowMenu.toggle(event, item);
  }

  onRowAction(action: Action): void {
    const item = this.rowMenu?.data as CommunicationPreferences;
    if (!item) {
      return;
    }

    const index = this.contactMethod.indexOf(item);
    if (action.key === 'edit') {
      this.selectedDataToUpdate(item);
    } else if (action.key === 'delete' && index >= 0) {
      this.deletePreferences(item, index);
    }
  }

  private getMethodPreferenceLabel(method: string): string {
    return method?.toUpperCase() === 'EMAIL' ? 'email' : 'text message';
  }

  updateValueValidators(value: string): void {
    const valueText = this.fg.get('value');
    valueText?.setValue(null);
    valueText?.setErrors(null);

    if (value === 'EMAIL') {
      valueText?.setValidators([
        Validators.required,
        ValidatorsEx.email(this.fg.controls.value),
      ]);
    } else if (value === 'TEXT') {
      valueText?.setValidators([
        Validators.required,
        ValidatorsEx.phone(this.fg.controls.value),
      ]);
    }

    valueText?.updateValueAndValidity();
  }

  maskPhone(phone: string) {
    const phoneLength = phone.length - 4;
    const phoneMasked = new String('***-***-') + phone.substring(phoneLength);
    return phoneMasked;
  }

  maskEmail(email: string) {
    const emailSplit = email.split('@');
    const local = emailSplit[0];
    const domain = emailSplit[1];

    let maskedLocal: string;
    if (local.length <= 1) {
      maskedLocal = '*';
    } else if (local.length === 2) {
      maskedLocal = '*' + local[1];
    } else {
      const visibleCount = local.length - 2;
      maskedLocal = '*'.repeat(visibleCount) + local.substring(visibleCount);
    }

    return maskedLocal + '@' + domain;
  }
}
